import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Download, AlertCircle } from "lucide-react";
import { QRCode } from "react-qrcode-logo";
import html2canvas from "html2canvas";

import { useToast } from "@/hooks/use-toast";
import {
  fetchTableById,
  getPublicMenuUrl,
} from "@/services/tables/tableService";
import { StoreTable } from "@/models/tableModel";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface QrCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tableId: string | null;
}

export function QrCodeDialog({
  open,
  onOpenChange,
  tableId,
}: QrCodeDialogProps) {
  const [table, setTable] = useState<StoreTable | null>(null);
  const [qrValue, setQrValue] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    if (tableId) {
      fetchTable(tableId);
    }
  }, [tableId]);

  const fetchTable = async (id: string) => {
    setIsLoading(true);
    try {
      const tableData = await fetchTableById(id);
      if (tableData) {
        setTable(tableData);

        // Generate QR code URL for the menu with table ID
        const menuUrl = getPublicMenuUrl(id);
        setQrValue(menuUrl);
      }
    } catch (error) {
      console.error("Error fetching table:", error);
      toast({
        title: t("error"),
        description: t("failedToLoadTableDetails"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(qrValue);
    toast({
      title: t("linkCopied"),
      description: t("qrCodeLinkCopiedToClipboard"),
    });
  };

  const downloadQRCode = async () => {
    const qrContainer = document.getElementById("qr-container");
    if (!qrContainer) {
      return;
    }

    try {
      // Use html2canvas to capture the container
      const canvas = await html2canvas(qrContainer, {
        backgroundColor: "#FFFFFF",
        scale: 2, // Higher quality
      });

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error("Failed to create blob");
            return;
          }

          // Create download link
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `table-qr-${table?.number || "unknown"}.png`;
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          toast({
            title: t("qrCodeDownloaded"),
            description: t("qrCodeDownloadedSuccessfully"),
          });
        },
        "image/png",
        1.0
      );
    } catch (error) {
      console.error("Error downloading QR code:", error);
      toast({
        title: "Error",
        description: "Failed to download QR code. Please try again.",
        variant: "destructive",
      });
    }
  };

  const createTableNumberLogo = (tableNumber: string) => {
    const canvas = document.createElement("canvas");
    canvas.width = 50;
    canvas.height = 50;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";

    // Draw white circle background
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.arc(25, 25, 25, 0, Math.PI * 2);
    ctx.fill();

    // Draw border
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Add table number
    ctx.fillStyle = "#000000";
    ctx.font = "bold 24px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(tableNumber, 25, 25);

    return canvas.toDataURL();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isLoading
              ? t("loading")
              : table
              ? `${t("table")} ${table.number}: ${table.name}`
              : t("tableQrCode")}
          </DialogTitle>
          <DialogDescription>
            {t("scanQrCodeToAccessTableMenu")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center py-4">
          {isLoading ? (
            <div className="h-48 w-48 flex items-center justify-center bg-muted/50 rounded-md">
              {t("loading")}
            </div>
          ) : (
            <div
              id="qr-container"
              className="flex flex-col items-center p-6 bg-white rounded-lg shadow-sm border-2 border-gray-100"
            >
              {/* Header Text */}
              <div className="mb-4 text-center">
                <h2 className="text-xl font-bold text-gray-800">
                  SCAN TO ORDER & PAY
                </h2>
              </div>

              {/* QR Code */}
              <div className="mb-4 relative">
                <QRCode
                  id="qr-code"
                  size={200}
                  value={qrValue || "https://cultrpay.com"}
                  fgColor="#000"
                  bgColor="#fff"
                  ecLevel="H"
                  logoImage={
                    table
                      ? createTableNumberLogo(table.number.toString())
                      : undefined
                  }
                  logoWidth={50}
                  logoHeight={50}
                  eyeColor="#000"
                  eyeRadius={[10, 10, 10, 10]}
                  qrStyle="squares"
                  quietZone={10}
                  logoPaddingStyle="circle"
                />
              </div>

              {/* Table ID Display */}
              <div className="text-center mb-2">
                <p className="text-sm font-semibold text-gray-700">
                  Table ID: {tableId?.substring(0, 8)}...
                </p>
                <p className="text-xs text-gray-500">
                  {table ? `Table ${table.number}: ${table.name}` : "Table"}
                </p>
              </div>

              {/* Powered by Xerven */}
              <div className="text-center">
                <p className="text-xs text-purple-600 font-medium">
                  Powered by Xerven
                </p>
              </div>
            </div>
          )}

          <Alert className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              {t("scanningThisCodeWillDirectlyOpenTheMenuForThisSpecificTable")}
            </AlertDescription>
          </Alert>

          <div className="flex gap-2 mt-4 w-full">
            <Button
              variant="outline"
              onClick={copyToClipboard}
              disabled={!qrValue}
              className="flex-1"
            >
              <Copy className="mr-2 h-4 w-4" />
              {t("copyLink")}
            </Button>
            <Button
              variant="outline"
              onClick={downloadQRCode}
              disabled={!qrValue}
              className="flex-1"
            >
              <Download className="mr-2 h-4 w-4" />
              {t("download")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
